/**
 * Optimized Dashboard Component
 * 
 * Uses single dashboard API call instead of 100+ individual requests
 * Dramatically improves loading performance
 */

import React, { useState, useMemo, memo, Component } from 'react';
import { Card, Row, Col, Modal, Button, Spin, Alert } from 'antd';
import { FullscreenOutlined } from '@ant-design/icons';
import { useDashboardData } from '../hooks/useDashboardData';
import { CostTrendCard } from './Analytics/CostTrendCard';
import DailyQuestionsSessions from './Analytics/DailyQuestionsSessionsChart';
import UserFeedbackAnalysis from './Analytics/UserFeedbackAnalysis';
import UserEngagementFunnel from './Analytics/UserEngagementFunnel';
import CommonWordsAnalysis from './Analytics/CommonWordsAnalysis';
import CategoryDistribution from './Analytics/CategoryDistribution';
import SemanticTopicAnalysis from './Analytics/SemanticTopicAnalysis';
import AITrendsCard from './Analytics/AITrendsCard';
import ResponseTimeAnalytics from './Analytics/ResponseTimeAnalytics';
import SessionQualityMetrics from './Analytics/SessionQualityMetrics';
import SourceDistribution from './Analytics/SourceDistribution';
import QuestionComplexityAnalysis from './Analytics/QuestionComplexityAnalysis';
import CitationUsageAnalytics from './Analytics/CitationUsageAnalytics';
import HourlyActivityHeatmap from './Analytics/HourlyActivityHeatmap';

// Error boundary to prevent a single card crash from taking down the whole dashboard
interface ErrorBoundaryProps {
  cardTitle: string;
  children: React.ReactNode;
}
interface ErrorBoundaryState {
  hasError: boolean;
}
class CardErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error(`[Dashboard] ${this.props.cardTitle} crashed:`, error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <Alert
          type="warning"
          message={`${this.props.cardTitle} failed to render`}
          description="This card encountered an error. Other dashboard cards are unaffected."
          showIcon
        />
      );
    }
    return this.props.children;
  }
}

// Component wrappers for collapsed/expanded modes
const DailyQuestionsSessionsSection = ({ dashboardData }: { dashboardData: any }) => (
  <DailyQuestionsSessions defaultAggregation="daily" isExpanded={false} dashboardData={dashboardData} />
);
const DailyQuestionsSessionsExpanded = () => (
  <DailyQuestionsSessions defaultAggregation="daily" isExpanded={true} />
);
const FeedbackAnalysisSection = ({ dashboardData }: { dashboardData: any }) => (
  <UserFeedbackAnalysis isExpanded={false} dashboardData={dashboardData} />
);
const FeedbackAnalysisExpanded = () => (
  <UserFeedbackAnalysis isExpanded={true} />
);
const EngagementFunnelSection = ({ dashboardData }: { dashboardData: any }) => (
  <UserEngagementFunnel isExpanded={false} dashboardData={dashboardData} />
);
const EngagementFunnelExpanded = () => (
  <UserEngagementFunnel isExpanded={true} />
);
const CommonWordsAnalysisSection = ({ dashboardData }: { dashboardData: any }) => (
  <CommonWordsAnalysis isExpanded={false} dashboardData={dashboardData} />
);
const CommonWordsAnalysisExpanded = () => (
  <CommonWordsAnalysis isExpanded={true} />
);

const CategoryDistributionSection = ({ dashboardData }: { dashboardData: any }) => (
  <CategoryDistribution isExpanded={false} dashboardData={dashboardData} />
);
const CategoryDistributionExpanded = () => (
  <CategoryDistribution isExpanded={true} />
);
const SemanticTopicAnalysisSection = ({ dashboardData }: { dashboardData: any }) => (
  <SemanticTopicAnalysis isExpanded={false} dashboardData={dashboardData} />
);
const SemanticTopicAnalysisExpanded = () => (
  <SemanticTopicAnalysis isExpanded={true} />
);
const CostTrendSection = ({ dashboardData }: { dashboardData: any }) => (
  <CostTrendCard isExpanded={false} dashboardData={dashboardData} />
);
const CostTrendExpanded = () => (
  <CostTrendCard isExpanded={true} />
);
const AITrendsSection = ({ dashboardData }: { dashboardData: any }) => (
  <AITrendsCard isExpanded={false} dashboardData={dashboardData} />
);
const AITrendsExpanded = () => (
  <AITrendsCard isExpanded={true} />
);

// New Enhanced Analytics Components
const ResponseTimeSection = ({ dashboardData }: { dashboardData: any }) => (
  <ResponseTimeAnalytics isExpanded={false} dashboardData={dashboardData} />
);
const ResponseTimeExpanded = () => (
  <ResponseTimeAnalytics isExpanded={true} />
);
const SessionQualitySection = ({ dashboardData }: { dashboardData: any }) => (
  <SessionQualityMetrics isExpanded={false} dashboardData={dashboardData} />
);
const SessionQualityExpanded = () => (
  <SessionQualityMetrics isExpanded={true} />
);
const SourceDistributionSection = ({ dashboardData }: { dashboardData: any }) => (
  <SourceDistribution isExpanded={false} dashboardData={dashboardData} />
);
const SourceDistributionExpanded = () => (
  <SourceDistribution isExpanded={true} />
);
const QuestionComplexitySection = ({ dashboardData }: { dashboardData: any }) => (
  <QuestionComplexityAnalysis isExpanded={false} dashboardData={dashboardData} />
);
const QuestionComplexityExpanded = () => (
  <QuestionComplexityAnalysis isExpanded={true} />
);
const CitationUsageSection = ({ dashboardData }: { dashboardData: any }) => (
  <CitationUsageAnalytics isExpanded={false} dashboardData={dashboardData} />
);
const CitationUsageExpanded = () => (
  <CitationUsageAnalytics isExpanded={true} />
);
const HourlyActivitySection = ({ dashboardData }: { dashboardData: any }) => (
  <HourlyActivityHeatmap isExpanded={false} dashboardData={dashboardData} />
);
const HourlyActivityExpanded = () => (
  <HourlyActivityHeatmap isExpanded={true} />
);

const OptimizedDashboard: React.FC = memo(() => {
  const [expandedCard, setExpandedCard] = useState<string | null>(null);
  const { data: dashboardData, loading, error, refresh } = useDashboardData();

  const vizCards = [
    // ROW 1: Primary Metrics (1/2 + 1/2)
    {
      key: "activityTrend",
      title: "Chatbot Activity Trend",
      component: DailyQuestionsSessionsSection,
      expandedComponent: DailyQuestionsSessionsExpanded,
      size: { xs: 24, lg: 12 },
    },
    {
      key: "costTrend",
      title: "Cost Trend",
      component: CostTrendSection,
      expandedComponent: CostTrendExpanded,
      size: { xs: 24, lg: 12 },
    },

    // ROW 2: Analytics (1/2 + 1/2) - Feedback needs more space
    {
      key: "feedbackAnalysis",
      title: "User Feedback Analysis",
      component: FeedbackAnalysisSection,
      expandedComponent: FeedbackAnalysisExpanded,
      size: { xs: 24, lg: 12 },
    },
    {
      key: "engagementFunnel",
      title: "User Engagement Funnel",
      component: EngagementFunnelSection,
      expandedComponent: EngagementFunnelExpanded,
      size: { xs: 24, lg: 12 },
    },

    // ROW 4: Category Distribution (1/2 width)
    {
      key: "categoryDistribution",
      title: "Category Distribution",
      component: CategoryDistributionSection,
      expandedComponent: CategoryDistributionExpanded,
      size: { xs: 24, lg: 12 },
    },

    // ROW 5: Content Analysis (1/2 + 1/2)
    {
      key: "commonWords",
      title: "Common Words Analysis",
      component: CommonWordsAnalysisSection,
      expandedComponent: CommonWordsAnalysisExpanded,
      size: { xs: 24, lg: 12 },
    },


    // ROW 6: AI Insights (1/2 + 1/2)
    {
      key: "semanticTopicAnalysis",
      title: "Semantic Topic Analysis",
      component: SemanticTopicAnalysisSection,
      expandedComponent: SemanticTopicAnalysisExpanded,
      size: { xs: 24, lg: 12 },
    },

    {
      key: "aiTrends",
      title: "Trend Insights",
      component: AITrendsSection,
      expandedComponent: AITrendsExpanded,
      size: { xs: 24, lg: 24 },
    },

    // ROW 7: Enhanced Analytics - Performance & Quality (1/2 + 1/2)
    {
      key: "responseTime",
      title: "Response Time Analytics",
      component: ResponseTimeSection,
      expandedComponent: ResponseTimeExpanded,
      size: { xs: 24, lg: 12 },
    },
    {
      key: "sessionQuality",
      title: "Session Quality Metrics",
      component: SessionQualitySection,
      expandedComponent: SessionQualityExpanded,
      size: { xs: 24, lg: 12 },
    },

    // ROW 8: Enhanced Analytics - Traffic & Content (1/2 + 1/2)
    {
      key: "sourceDistribution",
      title: "Source Distribution",
      component: SourceDistributionSection,
      expandedComponent: SourceDistributionExpanded,
      size: { xs: 24, lg: 12 },
    },
    {
      key: "questionComplexity",
      title: "Question Complexity Analysis",
      component: QuestionComplexitySection,
      expandedComponent: QuestionComplexityExpanded,
      size: { xs: 24, lg: 12 },
    },

    // ROW 9: Enhanced Analytics - Citations & Activity (1/2 + 1/2)
    {
      key: "citationUsage",
      title: "Citation Usage Analytics",
      component: CitationUsageSection,
      expandedComponent: CitationUsageExpanded,
      size: { xs: 24, lg: 12 },
    },
    {
      key: "hourlyActivity",
      title: "Hourly Activity Heatmap",
      component: HourlyActivitySection,
      expandedComponent: HourlyActivityExpanded,
      size: { xs: 24, lg: 12 },
    },
  ];

  const getExpandedComponent = (cardKey: string) => {
    const card = vizCards.find(c => c.key === cardKey);
    return card ? card.expandedComponent : null;
  };

  // Memoize expensive operations - MUST be before any early returns
  const expandedCardTitle = useMemo(() => {
    return expandedCard ? vizCards.find(c => c.key === expandedCard)?.title : '';
  }, [expandedCard]);

  const ExpandedComponent = useMemo(() => {
    return expandedCard ? getExpandedComponent(expandedCard) : null;
  }, [expandedCard]);

  // Memoize modal styles to prevent recreation
  const modalStyles = useMemo(() => ({
    body: { 
      maxHeight: '80vh', 
      overflow: 'auto',
      padding: '24px'
    },
    header: {
      borderBottom: '1px solid #f0f0f0',
      padding: '16px 24px',
      fontSize: '18px',
      fontWeight: 600,
      color: '#262626'
    },
    mask: {
      backgroundColor: 'rgba(0, 0, 0, 0.45)'
    }
  }), []);

  // Show loading state
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 0' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16, color: '#666', fontWeight: 500 }}>
          Loading dashboard data...
        </div>
        <div style={{ marginTop: 8, color: '#999', fontSize: 13 }}>
          Fetching all dashboard data in a single request
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 0' }}>
        <div style={{ color: '#ff4d4f', marginBottom: 16 }}>
          Failed to load dashboard data
        </div>
        <Button type="primary" onClick={refresh}>
          Retry
        </Button>
      </div>
    );
  }

  const handleExpand = (cardKey: string) => {
    setExpandedCard(cardKey);
  };

  const handleClose = () => {
    setExpandedCard(null);
  };

  return (
    <div style={{ padding: '24px', paddingBottom: '80px' }}>
      {/* Dashboard Header */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>Analytics Dashboard</h2>
      </div>

      {/* Dashboard Cards */}
      <Row gutter={[24, 24]} style={{ marginBottom: 40 }}>
        {vizCards.map((card) => {
          const CardComponent = card.component;
          return (
            <Col key={card.key} {...card.size}>
              <Card
                title={card.title}
                extra={
                  <Button
                    type="text"
                    icon={<FullscreenOutlined />}
                    onClick={() => handleExpand(card.key)}
                    size="small"
                  >
                    Expand
                  </Button>
                }
                style={{ 
                  height: '100%',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                  border: '1px solid #f0f0f0'
                }}
                variant="outlined"
                styles={{
                  header: {
                    borderBottom: '1px solid #f0f0f0',
                    padding: '16px 20px',
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#262626'
                  },
                  body: {
                    padding: '20px',
                    height: 'calc(100% - 57px)',
                    display: 'flex',
                    flexDirection: 'column'
                  }
                }}
              >
                <CardErrorBoundary cardTitle={card.title}>
                  <CardComponent dashboardData={dashboardData} />
                </CardErrorBoundary>
              </Card>
            </Col>
          );
        })}
      </Row>

      {/* Expanded Modal */}
      <Modal
        title={expandedCardTitle}
        open={!!expandedCard}
        onCancel={handleClose}
        footer={null}
        width={1200}
        style={{ top: 20 }}
        styles={modalStyles}
      >
        {ExpandedComponent && <ExpandedComponent />}
      </Modal>
    </div>
  );
});

OptimizedDashboard.displayName = 'OptimizedDashboard';

export default OptimizedDashboard;
